// M3 economy & politics: the daily entry point for tick.js, the news-effect handler for events.js, the decision
// kinds for core/actions.js and the hooks production.js / market.js / m2 call. Inert before 1946.
import { dayToDate } from '../../core/time.js';
import { chance } from '../../core/rng.js';
import { econState, m3On } from './econ.js';
import { applyNationalization, tickNationalizations, occupationShutIns, applyWellFires, tickWellFires, resolveParticipationImpl, resolveWellFireContractImpl } from './nationalize.js';
import { openRound, tickRounds, tickLicences } from './leaserounds.js';
import { tickHedging } from './hedging.js';
import { tickRegimes, chargeSpill, scriptedSpill } from './regimes.js';
import { addEmbargo, tankerLossRoll, saleBlocked } from './routes.js';
import { chargeFiscal } from './fiscal.js';
import { notify } from '../util.js';
import { yf } from '../defs.js';

// tick.js: after tickM2 (so the M2 closure path has seized first), before tickFinance.
export function tickM3Econ(state) {
  if (!m3On(state)) return;
  const { dom } = dayToDate(state.day);
  tickNationalizations(state);
  occupationShutIns(state);
  tickWellFires(state);
  tickRounds(state);
  tickLicences(state);
  tickHedging(state);
  tickRegimes(state, dom);
}

// events.js tickNews: the dormant M3 effect types this agent owns (nationalization, leaseRound, suez, route,
// embargo, wellFires, regime). `storm` and `megaproject` belong to the ops sim.
// polish (C2): display names for embargo destination ids.
const DEST_NAMES = { us: 'the United States', uk: 'Britain', netherlands: 'the Netherlands', japan: 'Japan', europe: 'Europe' };
const destList = (ids = []) => { const n = ids.map((d) => DEST_NAMES[d] ?? d); return n.length > 1 ? n.slice(0, -1).join(', ') + ' and ' + n[n.length - 1] : n.join(''); };
export function applyM3EconNewsEffect(state, item) {
  if (!m3On(state) || !item) return;
  scriptedSpill(state, item.id); // Ixtoc, Exxon Valdez: a slick for the renderers (not the player's)
  for (const e of [item.effect, item.m3Effect]) {
    if (!e) continue;
    switch (e.type) {
      case 'nationalization': applyNationalization(state, e.id, { fromNews: true }); break;
      case 'leaseRound': openRound(state, e.roundId); break;
      case 'embargo': {
        const rec = addEmbargo(state, item, e);
        if (rec) notify(state, 'embargo', { regionIds: rec.regionIds, markets: rec.markets, untilDay: rec.untilDay, level: 'warn', text: `Embargo: crude from ${rec.regionIds.length} Arab producers may not be sold to ${destList(rec.destinations)} for ${Math.round((rec.untilDay - rec.fromDay) / 30)} months. Cargoes are rerouted.` });
        break;
      }
      case 'wellFires': applyWellFires(state, e); break;
      case 'regime': econState(state).regimes[e.id] = state.day; break;
      case 'suez': case 'route': econState(state).regimes[`${e.type}:${e.id ?? (e.open ? 'open' : 'closed')}`] = state.day; break; // tables are the source of truth
      default: break;
    }
  }
}

// core/actions.resolveDecision
export const M3_ECON_DECISION_KINDS = new Set(['participation', 'wellFireContract']);
export function resolveM3EconDecision(state, d, choice) {
  if (d.kind === 'participation') return resolveParticipationImpl(state, d, choice);
  if (d.kind === 'wellFireContract') return resolveWellFireContractImpl(state, d, choice);
  return { ok: false, msg: 'Unknown decision.' };
}

// production.shipAndSell, after the M2 onSale: fiscal take, windfall tax, tanker casualties.
export function onSaleM3(state, fieldId, bbl, gross, nb, opexPerWell = 0) {
  if (!m3On(state) || !(bbl > 0)) return;
  chargeFiscal(state, fieldId, bbl, gross, nb, opexPerWell);
  const spilled = tankerLossRoll(state, fieldId, bbl, (p) => chance(state, p));
  if (spilled > 0) chargeSpill(state, fieldId, spilled, 'tanker casualty'); // record: fieldState.spill
}

export { saleBlocked };
export const m3Year = (state) => yf(state);
